import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';

// Persists across DOM replacements (morphing) to keep option-mode items visible
const optionModeState = { variants: new Set(), products: new Set() };

// Dynamic <style> element to hide option-mode cart items via CSS (no flicker)
const optionModeStyleEl = document.createElement('style');
optionModeStyleEl.id = 'option-mode-cart-hide';
document.head.appendChild(optionModeStyleEl);

function updateOptionModeCSS() {
  if (optionModeState.variants.size === 0) {
    optionModeStyleEl.textContent = '';
    return;
  }
  const selectors = [];
  optionModeState.variants.forEach(vid => {
    selectors.push(`tr.cart-items__table-row[data-variant-id="${vid}"]`);
  });
  optionModeStyleEl.textContent = `${selectors.join(', ')} { display: none !important; }`;
}

// Devise active du marché (Shopify Markets) — plus de symbole codé en dur
function tdmActiveCurrency() {
  return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
}
function tdmCurrencyRate() {
  return parseFloat((window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || '1') || 1;
}
function tdmMoneyFormatter() {
  const locale = (window.Shopify && window.Shopify.locale) || document.documentElement.lang || 'fr';
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: tdmActiveCurrency() });
  } catch (e) {
    return new Intl.NumberFormat('fr', { style: 'currency', currency: 'EUR' });
  }
}

// cart.js renvoie déjà les montants dans la devise de présentation active → pas de conversion
function formatCartPrice(cents) {
  return tdmMoneyFormatter().format((cents || 0) / 100);
}

async function refreshCartDisplay() {
  try {
    const rootUrl = window.Shopify?.routes?.root || '/';
    const resp = await fetch(`${rootUrl}cart.js`);
    if (!resp.ok) return;
    const cart = await resp.json();

    // Update total price
    const totalStr = formatCartPrice(cart.total_price);
    document.querySelectorAll('[data-cart-subtotal]').forEach(el => {
      el.textContent = totalStr;
      if (el.getAttribute('value') !== null) el.setAttribute('value', totalStr);
    });

    // Update cart count badge
    document.querySelectorAll('.cart-bubble__text-count').forEach(el => {
      el.textContent = cart.item_count < 100 ? String(cart.item_count) : '';
    });

    // Calculate original price & savings (including compare_at_price)
    let saleSavings = 0;
    cart.items.forEach(item => {
      if (item.compare_at_price && item.compare_at_price > item.price) {
        saleSavings += (item.compare_at_price - item.price) * item.quantity;
      }
    });
    const fullOriginal = cart.original_total_price + saleSavings;
    const totalDiscount = fullOriginal - cart.total_price;

    // Update original (strikethrough) price
    document.querySelectorAll('.cart__original-price').forEach(el => {
      if (totalDiscount > 0) {
        el.textContent = formatCartPrice(fullOriginal);
        el.style.display = '';
      } else {
        el.textContent = '';
        el.style.display = 'none';
      }
    });

    // Update savings text
    document.querySelectorAll('.cart__savings-text').forEach(el => {
      if (totalDiscount > 0) {
        const match = el.textContent.trim().match(/^(.+?)\s+[\d]/);
        const label = match ? match[1].trim() : 'Vous économisez';
        el.textContent = `${label} ${formatCartPrice(totalDiscount)}`;
        el.style.display = '';
      } else {
        el.style.display = 'none';
      }
    });

    // Update discount expiry visibility
    document.querySelectorAll('.cart__discount-expiry').forEach(el => {
      el.style.display = totalDiscount > 0 ? '' : 'none';
    });
  } catch (error) {
    console.error('Error refreshing cart display:', error);
  }
}

/**
* Cart Recommendations Component
* Manages carousel or inline display of recommended products and adding items to cart
*/
class CartRecommendationsComponent extends Component {
 connectedCallback() {
   try {
     if (this._initialized) return;
     this._initialized = true;

     super.connectedCallback();
     this.scrollAmount = 200;
     this.collectionHandle = this.dataset.collectionHandle || '';
     this.displayStyle = this.dataset.style || 'carousel';
     this.inlineControl = this.dataset.inlineControl || 'checkbox';
     this.inlineBehavior = this.dataset.inlineBehavior || 'add';
     this._loadId = 0;

     const productId = this.dataset.productId;
     const sectionId = this.dataset.sectionId || 'cart-drawer';

     if (this.collectionHandle) {
       this.loadCollectionProducts(this.collectionHandle);
     } else if (productId) {
       this.loadRecommendations(productId, sectionId);
     }

     this.boundHandleCartUpdate = this.handleCartUpdate.bind(this);
     document.addEventListener(ThemeEvents.cartAdd, this.boundHandleCartUpdate);
     document.addEventListener(ThemeEvents.cartUpdate, this.boundHandleCartUpdate);

     this.addEventListener('click', (e) => {
       const prev = e.target.closest('[ref="prevButton"]');
       const next = e.target.closest('[ref="nextButton"]');
       if (prev) this.scrollPrev();
       if (next) this.scrollNext();

       const plusBtn = e.target.closest('.cart-recommendations__inline-plus-btn');
       if (plusBtn) {
         e.preventDefault();
         e.stopPropagation();
         this.handlePlusAdd(plusBtn);
       }
     });

     this.addEventListener('change', (e) => {
       const control = e.target.closest('.cart-recommendations__inline-checkbox') || e.target.closest('.cart-recommendations__inline-toggle-input');
       if (control) this.handleInlineAdd(control);
     });
   } catch (error) {
     console.error('Error initializing cart recommendations:', error);
   }
 }

 get track() { return this.querySelector('[ref="track"]'); }
 get prevButton() { return this.querySelector('[ref="prevButton"]'); }
 get nextButton() { return this.querySelector('[ref="nextButton"]'); }
 get itemsContainer() { return this.querySelector('[ref="itemsContainer"]'); }

 disconnectedCallback() {
   if (this.boundHandleCartUpdate) {
     document.removeEventListener(ThemeEvents.cartAdd, this.boundHandleCartUpdate);
     document.removeEventListener(ThemeEvents.cartUpdate, this.boundHandleCartUpdate);
   }
 }

 async handleCartUpdate(event) {
   const currentLoadId = ++this._loadId;

   try {
     const rootUrl = window.Shopify?.routes?.root || '/';
     const cartResponse = await fetch(`${rootUrl}cart.js`);
     if (!cartResponse.ok) throw new Error('Failed to fetch cart');
     if (currentLoadId !== this._loadId) return;

     const cart = await cartResponse.json();

     if (cart.items && cart.items.length > 0) {
       const firstProductId = cart.items[0].product_id;
       this.dataset.productId = firstProductId;
       this.style.display = '';

       if (this.collectionHandle) {
         await this.loadCollectionProducts(this.collectionHandle, currentLoadId);
       } else {
         await this.loadRecommendations(firstProductId, this.dataset.sectionId || 'cart-drawer', currentLoadId);
       }
     } else {
       this.style.display = 'none';
     }
   } catch (error) {
     console.error('Error handling cart update:', error);
   }
 }

 async loadRecommendations(productId, sectionId, loadId) {
   try {
     const rootUrl = window.Shopify?.routes?.root || '/';
     const url = `${rootUrl}recommendations/products.json?product_id=${productId}&limit=8&intent=related`;

     const response = await fetch(url);
     if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
     if (loadId != null && loadId !== this._loadId) return;

     const data = await response.json();
     const products = data.products || [];

     const container = this.itemsContainer;
     if (products.length > 0 && container) {
       const cartProductIds = await this.getCartProductIds();
       if (loadId != null && loadId !== this._loadId) return;

       const filteredProducts = products.filter(product =>
         !cartProductIds.includes(product.id) || optionModeState.products.has(product.id)
       );

       if (filteredProducts.length > 0) {
         this.renderProducts(container, filteredProducts);
       } else {
         this.style.display = 'none';
       }
     } else {
       this.style.display = 'none';
     }
   } catch (error) {
     console.error('Error loading recommendations:', error);
     this.style.display = 'none';
   }
 }

 async loadCollectionProducts(collectionHandle, loadId) {
   try {
     const rootUrl = window.Shopify?.routes?.root || '/';
     const url = `${rootUrl}collections/${collectionHandle}/products.json?limit=20`;

     const response = await fetch(url);
     if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
     if (loadId != null && loadId !== this._loadId) return;

     const data = await response.json();
     const products = data.products || [];

     const container = this.itemsContainer;
     if (products.length > 0 && container) {
       const cartProductIds = await this.getCartProductIds();
       if (loadId != null && loadId !== this._loadId) return;

       const filteredProducts = products.filter(product =>
         !cartProductIds.includes(product.id) || optionModeState.products.has(product.id)
       );

       if (filteredProducts.length > 0) {
         const adaptedProducts = filteredProducts.map(product => {
           const variantPrice = parseFloat(product.variants?.[0]?.price || '0') * 100;
           const adaptedVariants = product.variants?.map(v => ({
             ...v,
             price: parseFloat(v.price || '0') * 100,
             compare_at_price: v.compare_at_price ? parseFloat(v.compare_at_price) * 100 : null
           }));
           return {
             id: product.id,
             title: product.title,
             handle: product.handle,
             featured_image: product.images?.[0]?.src || product.image?.src || null,
             variants: adaptedVariants,
             price: variantPrice
           };
         });
         this.renderProducts(container, adaptedProducts);
       } else {
         this.style.display = 'none';
       }
     } else {
       this.style.display = 'none';
     }
   } catch (error) {
     console.error('Error loading collection products:', error);
     this.style.display = 'none';
   }
 }

 renderProducts(container, products) {
   const isInline = this.displayStyle === 'inline';
   const maxProducts = isInline ? (parseInt(this.dataset.inlineMax, 10) || 2) : products.length;
   const productsToShow = products.slice(0, maxProducts);

   container.innerHTML = '';
   productsToShow.forEach(product => {
     if (isInline) {
       container.appendChild(this.createInlineCard(product));
     } else {
       container.appendChild(this.createProductCard(product));
     }
   });

   if (!isInline) {
     this.setupNavVisibility();
     this.updateNavVisibility();
   }

   // Restore checked state for option-mode items after render (survives morph)
   if (this.inlineBehavior === 'option' && optionModeState.variants.size > 0) {
     optionModeState.variants.forEach(vid => {
       const control = container.querySelector(`[data-variant-id="${vid}"]`);
       if (control && (control.type === 'checkbox' || control.tagName === 'INPUT')) {
         control.checked = true;
         if (control.classList.contains('cart-recommendations__inline-checkbox')) {
           control.classList.add('cart-recommendations__inline-checkbox--checked-anim');
         }
       }
     });
   }

   this.style.display = '';
 }

 async getCartProductIds() {
   try {
     const rootUrl = window.Shopify?.routes?.root || '/';
     const response = await fetch(`${rootUrl}cart.js`);
     if (!response.ok) {
       throw new Error('Failed to fetch cart');
     }

     const cart = await response.json();
     const productIds = cart.items.map(item => item.product_id);

     return productIds;
   } catch (error) {
     console.error('Error getting cart product IDs:', error);
     return [];
   }
 }

 createProductCard(product) {
   const item = document.createElement('div');
   item.className = 'cart-recommendations__item';

   const variant = product.variants?.[0];
   const imageUrl = product.featured_image || product.images?.[0];
   const variantPrice = variant?.price || product.price;
   const compareAtPrice = variant?.compare_at_price;
   const price = this.formatMoney(variantPrice);
   const variantId = variant?.id || '';
   const escapedTitle = this.escapeHtml(product.title);

   let priceHtml;
   if (compareAtPrice && compareAtPrice > variantPrice) {
     priceHtml = `<s class="cart-recommendations__compare-price">${this.formatMoney(compareAtPrice)}</s> ${price}`;
   } else {
     priceHtml = price;
   }

   const rootUrl = window.Shopify?.routes?.root || '/';
   item.innerHTML = `
     <div class="cart-recommendations__card">
       <a href="${rootUrl}products/${product.handle}" class="cart-recommendations__link">
         <div class="cart-recommendations__image-wrapper">
           ${imageUrl ? `
             <img
               src="${imageUrl}"
               alt="${escapedTitle}"
               loading="lazy"
               width="200"
               height="200"
               class="cart-recommendations__image"
             >
           ` : `
             <div class="cart-recommendations__no-image">
               <svg viewBox="0 0 525.5 525.5" xmlns="http://www.w3.org/2000/svg">
                 <path d="M324.5 212.7H203c-1.6 0-2.8 1.3-2.8 2.8V308c0 1.6 1.3 2.8 2.8 2.8h121.6c1.6 0 2.8-1.3 2.8-2.8v-92.5c0-1.6-1.3-2.8-2.9-2.8zm1.1 95.3c0 .6-.5 1.1-1.1 1.1H203c-.6 0-1.1-.5-1.1-1.1v-92.5c0-.6.5-1.1 1.1-1.1h121.6c.6 0 1.1.5 1.1 1.1V308z"></path>
                 <path d="M210.4 299.5H240v.1s.1 0 .2-.1h75.2v-76.2h-105v76.2zm1.8-7.2l20-20c1.6-1.6 3.8-2.5 6.1-2.5s4.5.9 6.1 2.5l1.5 1.5 16.8 16.8c-12.9 3.3-20.7 6.3-22.8 7.2h-27.7v-5.5zm101.5 5.4h-68.9c9.4-4.5 26.1-10.4 32.4-12.2l11.8 11.8c.4.4.9.6 1.4.6s1-.2 1.4-.6c.8-.8.8-2 0-2.8l-13.5-13.5c-.8-.8-2-.8-2.8 0-.8.8-.8 2 0 2.8l1.5 1.5-7.1 2.2c-1.5.5-3.5 1.1-5.8 1.8l-25.3-25.3c-2.4-2.4-5.6-3.7-8.9-3.7s-6.5 1.3-8.9 3.7l-18.2 18.2V225h101.5v72.7z"></path>
                 <path d="M232.8 254c4.6 0 8.3-3.7 8.3-8.3s-3.7-8.3-8.3-8.3-8.3 3.7-8.3 8.3 3.7 8.3 8.3 8.3zm0-14.9c3.6 0 6.6 2.9 6.6 6.6 0 3.6-2.9 6.6-6.6 6.6-3.6 0-6.6-2.9-6.6-6.6-.1-3.7 3-6.6 6.6-6.6z"></path>
               </svg>
             </div>
           `}
         </div>
         <div class="cart-recommendations__info">
           <p class="cart-recommendations__product-title">${escapedTitle}</p>
           <p class="cart-recommendations__price">
             ${priceHtml}
           </p>
         </div>
       </a>
       ${this.inlineControl === 'plus' ? `
         <button type="button" class="cart-recommendations__inline-plus-btn" data-variant-id="${variantId}" data-product-id="${product.id}" aria-label="Ajouter ${escapedTitle} au panier">
           <svg class="cart-recommendations__plus-icon" width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
         </button>
       ` : `
         <input
           type="checkbox"
           class="cart-recommendations__inline-checkbox"
           data-variant-id="${variantId}"
           data-product-id="${product.id}"
           aria-label="Ajouter ${escapedTitle} au panier"
         >
       `}
     </div>
   `;

   return item;
 }

 createInlineCard(product) {
   const item = document.createElement('div');
   item.className = 'cart-recommendations__inline-item';

   const variant = product.variants?.[0];
   const imageUrl = product.featured_image || product.images?.[0];
   const variantPrice = variant?.price || product.price;
   const compareAtPrice = variant?.compare_at_price;
   const price = this.formatMoney(variantPrice);
   const variantId = variant?.id || '';
   const escapedTitle = this.escapeHtml(product.title);

   let priceHtml;
   if (compareAtPrice && compareAtPrice > variantPrice) {
     priceHtml = `<s class="cart-recommendations__inline-compare-price">${this.formatMoney(compareAtPrice)}</s> ${price}`;
   } else {
     priceHtml = price;
   }

   let controlHtml;
   if (this.inlineControl === 'toggle') {
     controlHtml = `
       <label class="cart-recommendations__inline-toggle">
         <input
           type="checkbox"
           class="cart-recommendations__inline-toggle-input"
           data-variant-id="${variantId}"
           data-product-id="${product.id}"
           aria-label="Ajouter ${escapedTitle} au panier"
         >
         <span class="cart-recommendations__toggle-track">
           <span class="cart-recommendations__toggle-thumb"></span>
         </span>
       </label>
     `;
   } else if (this.inlineControl === 'plus') {
     controlHtml = `
       <button type="button" class="cart-recommendations__inline-plus-btn" data-variant-id="${variantId}" data-product-id="${product.id}" aria-label="Ajouter ${escapedTitle} au panier">
         <svg class="cart-recommendations__plus-icon" width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
       </button>
     `;
   } else {
     controlHtml = `
       <input
         type="checkbox"
         class="cart-recommendations__inline-checkbox"
         data-variant-id="${variantId}"
         data-product-id="${product.id}"
         aria-label="Ajouter ${escapedTitle} au panier"
       >
     `;
   }

   const rootUrl = window.Shopify?.routes?.root || '/';
   item.innerHTML = `
     <div class="cart-recommendations__inline-media">
       <a href="${rootUrl}products/${product.handle}">
         ${imageUrl ? `
           <img
             src="${imageUrl}"
             alt="${escapedTitle}"
             loading="lazy"
             width="120"
             height="120"
           >
         ` : ''}
       </a>
     </div>
     <div class="cart-recommendations__inline-details">
       <a href="${rootUrl}products/${product.handle}">
         <p class="cart-recommendations__inline-title">${escapedTitle}</p>
       </a>
       <p class="cart-recommendations__inline-price">${priceHtml}</p>
     </div>
     <div class="cart-recommendations__inline-action">
       ${controlHtml}
     </div>
   `;

   return item;
 }

 async handleInlineAdd(control) {
   const variantId = control.dataset.variantId;
   if (!variantId) return;

   const isToggle = control.classList.contains('cart-recommendations__inline-toggle-input');
   const isOptionMode = this.inlineBehavior === 'option';

   // Option mode: uncheck → remove from cart
   if (isOptionMode && !control.checked) {
     return this.handleOptionRemove(control, variantId, isToggle);
   }

   // Normal flow: ignore if not checked
   if (!control.checked) return;

   if (isToggle) {
     if (control._adding) return;
     control._adding = true;
     const label = control.closest('.cart-recommendations__inline-toggle');
     if (label) label.style.pointerEvents = 'none';
   } else {
     control.disabled = true;
   }

   if (isToggle) {
     const thumb = control.parentElement.querySelector('.cart-recommendations__toggle-thumb');
     if (thumb) {
       thumb.addEventListener('animationend', () => {
         thumb.style.transition = 'none';
         thumb.classList.remove('cart-recommendations__toggle-thumb--pop');
         void thumb.offsetHeight;
         thumb.style.transition = '';
       }, { once: true });
       thumb.classList.add('cart-recommendations__toggle-thumb--pop');
     }
   } else {
     control.classList.remove('cart-recommendations__inline-checkbox--loading');
     control.classList.add('cart-recommendations__inline-checkbox--checked-anim');
   }

   try {
     const formData = new FormData();
     formData.append('id', variantId);
     formData.append('quantity', '1');

     // In option mode: do NOT request sections (avoids morphSection flicker)
     if (!isOptionMode) {
       const cartItemsComponents = document.querySelectorAll('cart-items-component');
       const sectionIds = [];
       cartItemsComponents.forEach((item) => {
         if (item instanceof HTMLElement && item.dataset.sectionId) {
           sectionIds.push(item.dataset.sectionId);
         }
       });
       if (sectionIds.length > 0) {
         formData.append('sections', sectionIds.join(','));
       }
     }

     const cartUrl = window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart/add.js` : '/cart/add.js';

     const response = await fetch(cartUrl, {
       method: 'POST',
       headers: { 'X-Requested-With': 'XMLHttpRequest' },
       body: formData
     });

     const data = await response.json();

     if (response.ok) {
       if (isOptionMode) {
         // Option mode: update state, CSS, and cart display WITHOUT dispatching events
         // This prevents morphSection from destroying/recreating the component
         const productId = control.dataset.productId;
         if (productId) optionModeState.products.add(parseInt(productId, 10));
         optionModeState.variants.add(variantId);
         updateOptionModeCSS();

         if (isToggle) {
           control._adding = false;
           const label = control.closest('.cart-recommendations__inline-toggle');
           if (label) label.style.pointerEvents = '';
         } else {
           control.disabled = false;
         }

         // Manually update cart total, count, savings — no events, no morph
         await refreshCartDisplay();
       } else {
         // Default mode: fade out
         const parentItem = control.closest('.cart-recommendations__inline-item') || control.closest('.cart-recommendations__item');
         if (parentItem) {
           parentItem.classList.add('cart-recommendations__inline-item--added');
         }

         await new Promise(resolve => setTimeout(resolve, 800));

         document.dispatchEvent(
           new CartAddEvent({}, 'cart-recommendations', {
             source: 'cart-recommendations',
             itemCount: 1,
             sections: data.sections
           })
         );
       }
     } else {
       control.checked = false;
       if (isToggle) {
         control._adding = false;
         const label = control.closest('.cart-recommendations__inline-toggle');
         if (label) label.style.pointerEvents = '';
       } else {
         control.disabled = false;
         control.classList.remove('cart-recommendations__inline-checkbox--checked-anim');
       }
       console.error('Error adding to cart:', data);
     }
   } catch (error) {
     control.checked = false;
     if (isToggle) {
       control._adding = false;
       const label = control.closest('.cart-recommendations__inline-toggle');
       if (label) label.style.pointerEvents = '';
     } else {
       control.disabled = false;
       control.classList.remove('cart-recommendations__inline-checkbox--checked-anim');
     }
     console.error('Error adding to cart:', error);
   }
 }

 async handleOptionRemove(control, variantId, isToggle) {
   // Optimistic: immediately update state and CSS before the fetch
   const productId = control.dataset.productId;
   if (productId) optionModeState.products.delete(parseInt(productId, 10));
   optionModeState.variants.delete(variantId);
   updateOptionModeCSS();

   if (!isToggle) {
     control.classList.remove('cart-recommendations__inline-checkbox--checked-anim');
   }

   try {
     // Do NOT request sections — avoids morphSection flicker
     const body = { id: variantId, quantity: 0 };

     const cartUrl = window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart/change.js` : '/cart/change.js';

     const response = await fetch(cartUrl, {
       method: 'POST',
       headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
       body: JSON.stringify(body)
     });

     const data = await response.json();

     if (response.ok) {
       // Manually update cart total, count, savings — no events, no morph
       await refreshCartDisplay();
     } else {
       // Revert on failure
       if (productId) optionModeState.products.add(parseInt(productId, 10));
       optionModeState.variants.add(variantId);
       updateOptionModeCSS();
       control.checked = true;
       console.error('Error removing from cart:', data);
     }
   } catch (error) {
     // Revert on failure
     if (productId) optionModeState.products.add(parseInt(productId, 10));
     optionModeState.variants.add(variantId);
     updateOptionModeCSS();
     control.checked = true;
     console.error('Error removing from cart:', error);
   }
 }

 async handlePlusAdd(button) {
   const variantId = button.dataset.variantId;
   if (!variantId || button.disabled) return;

   button.disabled = true;

   // Show checkmark immediately on click
   const plusIconHtml = button.innerHTML;
   button.innerHTML = '<svg class="cart-recommendations__check-icon" width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3.5 8.5l3 3 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

   try {
     const formData = new FormData();
     formData.append('id', variantId);
     formData.append('quantity', '1');

     const cartItemsComponents = document.querySelectorAll('cart-items-component');
     const sectionIds = [];
     cartItemsComponents.forEach((item) => {
       if (item instanceof HTMLElement && item.dataset.sectionId) {
         sectionIds.push(item.dataset.sectionId);
       }
     });

     if (sectionIds.length > 0) {
       formData.append('sections', sectionIds.join(','));
     }

     const cartUrl = window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart/add.js` : '/cart/add.js';

     const response = await fetch(cartUrl, {
       method: 'POST',
       headers: { 'X-Requested-With': 'XMLHttpRequest' },
       body: formData
     });

     const data = await response.json();

     if (response.ok) {
       const parentItem = button.closest('.cart-recommendations__inline-item') || button.closest('.cart-recommendations__item');
       if (parentItem) {
         parentItem.classList.add('cart-recommendations__inline-item--added');
       }

       await new Promise(resolve => setTimeout(resolve, 800));

       document.dispatchEvent(
         new CartAddEvent({}, 'cart-recommendations', {
           source: 'cart-recommendations',
           itemCount: 1,
           sections: data.sections
         })
       );
     } else {
       button.innerHTML = plusIconHtml;
       button.disabled = false;
       console.error('Error adding to cart:', data);
     }
   } catch (error) {
     button.innerHTML = plusIconHtml;
     button.disabled = false;
     console.error('Error adding to cart:', error);
   }
 }

 formatMoney(cents) {
   // Prix issus de /recommendations|/products.json : en devise de base boutique \u2192 convertir vers la devise active
   if (typeof cents === 'undefined' || cents === null) cents = 0;
   const amount = (cents / 100) * tdmCurrencyRate();
   return tdmMoneyFormatter().format(amount);
 }

 escapeHtml(text) {
   const div = document.createElement('div');
   div.textContent = text;
   return div.innerHTML;
 }

 setupNavVisibility() {
   if (this._navSetup) return;
   this._navSetup = true;

   const track = this.track;
   if (!track) return;

   track.addEventListener('scroll', () => this.updateNavVisibility());
 }

 updateNavVisibility() {
   const track = this.track;
   const prev = this.prevButton;
   const next = this.nextButton;
   if (!track || !prev || !next) return;

   const atStart = track.scrollLeft <= 0;
   const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;

   prev.style.display = atStart ? 'none' : '';
   next.style.display = atEnd ? 'none' : '';
 }

 scrollPrev() {
   if (this.track) {
     this.track.scrollBy({
       left: -this.scrollAmount,
       behavior: 'smooth'
     });
   }
 }


 scrollNext() {
   if (this.track) {
     this.track.scrollBy({
       left: this.scrollAmount,
       behavior: 'smooth'
     });
   }
 }
}


try {
 if (!customElements.get('cart-recommendations-component')) {
   customElements.define('cart-recommendations-component', CartRecommendationsComponent);
 }
} catch (error) {
 console.error('Error registering cart-recommendations-component:', error);
}

function ensureRecommendationsInit() {
 setTimeout(() => {
   document.querySelectorAll('cart-recommendations-component').forEach(component => {
     if (!component._initialized) {
       component.connectedCallback();
     }
   });
 }, 150);
}
document.addEventListener(ThemeEvents.cartAdd, ensureRecommendationsInit);
document.addEventListener(ThemeEvents.cartUpdate, ensureRecommendationsInit);